import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ConsentService } from 'src/app/services/consent.service';
import { CookiePreferencesComponent } from '../cookie-preferences/cookie-preferences.component';

/**
 * First-layer cookie notice.
 *
 * Three deliberate properties, each of which is what an enforcement action tends to turn on:
 *
 * 1. **Three equal buttons.** Accept, reject and configure are the same size, weight and colour.
 *    Refusing must not cost more clicks or less contrast than accepting — the January 2022 CNIL
 *    decisions against Google and Facebook were precisely about that asymmetry.
 * 2. **No dismiss affordance.** There is no X, no backdrop and no Esc handler, so the banner
 *    cannot be closed without answering and silence is never read as agreement.
 * 3. **Non-blocking.** It is a fixed strip rather than a modal: nothing non-essential loads until
 *    the user answers, but the site stays usable while they decide, so this is not a cookie wall.
 *
 * Rendered from `app.component.html`, the only host common to the logged-in shell and the
 * standalone pages (`verify-email`, `bind`) that sit outside it. Shown to signed-in users only —
 * see `ConsentService.needsCookieDecision` for why an anonymous visitor is not asked.
 */
@Component({
  selector: 'app-cookie-banner',
  templateUrl: './cookie-banner.component.html',
  standalone: true,
  imports: [CommonModule, MatButtonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieBannerComponent {
  private readonly consent = inject(ConsentService);
  private readonly dialog = inject(MatDialog);

  /** True while a signed-in user has never answered. Drives the banner's visibility. */
  readonly needsDecision = this.consent.needsCookieDecision;

  readonly isSaving = signal(false);

  acceptAll(): void {
    this.record(() => this.consent.acceptAllCookies());
  }

  rejectAll(): void {
    this.record(() => this.consent.rejectAllCookies());
  }

  configure(): void {
    this.dialog.open(CookiePreferencesComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      autoFocus: false,
    });
  }

  private record(action: () => Promise<void>): void {
    if (this.isSaving()) return;
    this.isSaving.set(true);
    // The banner hides on the local write, which happens before the server round trip, so the
    // user is never left waiting on the network to dismiss a notice they have already answered.
    action().finally(() => this.isSaving.set(false));
  }
}
